import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus, Truck, ShieldCheck, Sparkles } from "lucide-react";
import { http, fmtRON } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import ProductCard from "../components/ProductCard";

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState("");
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  useEffect(() => {
    http.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setActiveImg(r.data.cover_image);
      setQty(1);
    }).catch(() => {});
  }, [slug]);

  if (!product) return <div className="py-32 text-center">Se încarcă...</div>;

  const saving = product.original_price && product.original_price > product.price;
  const inStock = (product.stock || 0) > 0;

  const handleAdd = () => {
    add(product, qty);
    toast.success(`${product.title} a fost adăugată în coș ✨`);
  };

  return (
    <div className="py-12 md:py-16" data-testid="product-page">
      <div className="container-vdz">
        <div className="text-xs opacity-60 mb-8 font-serif-accent uppercase tracking-[0.18em]" data-testid="breadcrumbs">
          <Link to="/" className="hover:text-[color:var(--vdz-gold)]">Acasă</Link>
          <span className="mx-2">/</span>
          <Link to="/carti" className="hover:text-[color:var(--vdz-gold)]">Cărți</Link>
          <span className="mx-2">/</span>
          <span className="opacity-80">{product.title}</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 md:gap-16">
          {/* Gallery */}
          <div>
            <div className="gold-frame overflow-hidden" style={{ background: "var(--vdz-navy)" }}>
              <img src={activeImg} alt={product.title} className="w-full h-auto max-h-[640px] object-contain" data-testid="product-main-image" />
            </div>
            <div className="grid grid-cols-4 gap-3 mt-4" data-testid="product-gallery">
              {(product.gallery || []).map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(g)}
                  className={`aspect-[3/4] overflow-hidden border transition-all ${activeImg === g ? "border-[color:var(--vdz-gold)]" : "border-[color:var(--vdz-cream-border)]"}`}
                  data-testid={`product-gallery-thumb-${i}`}
                >
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div>
            {product.collection_label && (
              <span className="inline-block px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-serif-accent mb-5"
                    style={{ background: product.is_bundle ? "var(--vdz-burgundy)" : product.seasonal ? "var(--vdz-burnt-orange)" : "var(--vdz-navy)", color: "var(--vdz-cream)" }}>
                {product.collection_label}
              </span>
            )}
            <h1 className="font-serif-display text-4xl md:text-5xl leading-tight" style={{ color: "var(--vdz-navy)" }} data-testid="product-title">{product.title}</h1>
            {product.subtitle && <p className="mt-2 text-base opacity-70">{product.subtitle}</p>}

            <div className="flex items-end gap-4 mt-6">
              {saving && <div className="text-lg line-through opacity-50">{fmtRON(product.original_price)}</div>}
              <div className="font-serif-display text-5xl gold-text" data-testid="product-price">{fmtRON(product.price)}</div>
              {saving && (
                <span className="pb-2 text-xs uppercase tracking-[0.18em] font-serif-accent" style={{ color: "var(--vdz-burgundy)" }}>
                  Economisești {fmtRON(product.original_price - product.price)}
                </span>
              )}
            </div>

            <div className="mt-4 text-xs uppercase tracking-[0.18em] font-serif-accent" style={{ color: inStock ? "var(--vdz-gold)" : "var(--vdz-burgundy)" }} data-testid="product-stock">
              {inStock ? "● În stoc — Livrare 24-48h" : "Stoc epuizat"}
            </div>

            <p className="mt-6 leading-relaxed opacity-85" data-testid="product-description">{product.description}</p>

            {/* Specs */}
            <div className="mt-8 grid grid-cols-2 gap-4 text-sm">
              <div className="p-4 border" style={{ borderColor: "var(--vdz-cream-border)" }}>
                <div className="eyebrow">Desene</div>
                <div className="font-serif-display text-xl mt-1">{product.pages} desene</div>
              </div>
              <div className="p-4 border" style={{ borderColor: "var(--vdz-cream-border)" }}>
                <div className="eyebrow">Format</div>
                <div className="font-serif-display text-xl mt-1">{product.format}</div>
              </div>
            </div>

            {/* Qty & CTA */}
            <div className="mt-8 flex gap-4 items-stretch">
              <div className="flex items-center border" style={{ borderColor: "var(--vdz-cream-border)" }}>
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-full flex items-center justify-center hover:bg-[color:var(--vdz-cream-warm)]" data-testid="product-qty-decrease">
                  <Minus size={16} />
                </button>
                <span className="w-12 text-center font-serif-display text-lg" data-testid="product-qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="w-11 h-full flex items-center justify-center hover:bg-[color:var(--vdz-cream-warm)]" data-testid="product-qty-increase">
                  <Plus size={16} />
                </button>
              </div>
              <button onClick={handleAdd} disabled={!inStock} className="btn-gold flex-1" data-testid="add-to-cart-button">
                Adaugă în coș
              </button>
            </div>

            {/* Trust */}
            <div className="mt-8 space-y-3 text-sm">
              <div className="flex gap-3 items-start"><Truck size={16} className="shrink-0 mt-0.5" style={{ color: "var(--vdz-gold)" }} /> <span>Livrare prin curier în 24-48h. <strong>Gratuit peste 150 RON.</strong></span></div>
              <div className="flex gap-3 items-start"><ShieldCheck size={16} className="shrink-0 mt-0.5" style={{ color: "var(--vdz-gold)" }} /> <span>Plată la curier (ramburs) — plătești în numerar la primirea coletului.</span></div>
              <div className="flex gap-3 items-start"><Sparkles size={16} className="shrink-0 mt-0.5" style={{ color: "var(--vdz-gold)" }} /> <span>Carte originală creată de Alina Stancu, printată în România.</span></div>
            </div>
          </div>
        </div>

        {/* Related */}
        {product.related && product.related.length > 0 && (
          <div className="mt-24">
            <div className="eyebrow mb-3 text-center">Poate îți place și</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-center mb-12" style={{ color: "var(--vdz-navy)" }}>Alte cărți Vis de Zi</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {product.related.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
